import { useEffect, useRef, useState } from 'react';
import { LinkPreview as LinkPreviewApi } from '@/lib/api';

export interface PreviewData {
  url: string;
  siteName?: string;
  title?: string;
  description?: string;
  image?: string;
}

interface Props {
  url: string;
  className?: string;
}

const cache = new Map<string, PreviewData>();

export function getCachedPreview(url: string): PreviewData | undefined {
  return cache.get(url);
}

export default function LinkPreview({ url, className }: Props) {
  const [data, setData] = useState<PreviewData | undefined>(() => cache.get(url));
  const [loading, setLoading] = useState<boolean>(() => !cache.has(url));
  const [errored, setErrored] = useState(false);
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (cache.has(url)) {
      setData(cache.get(url));
      setLoading(false);
      return;
    }
    const reqId = ++reqIdRef.current;
    const ac = new AbortController();
    setLoading(true);
    setErrored(false);
    LinkPreviewApi.fetch(url, ac.signal)
      .then((res) => {
        if (reqId !== reqIdRef.current) return;
        const next: PreviewData = {
          url,
          siteName: res.siteName,
          title: res.title,
          description: res.description,
          image: res.image,
        };
        cache.set(url, next);
        setData(next);
        setLoading(false);
      })
      .catch(() => {
        if (reqId !== reqIdRef.current) return;
        setErrored(true);
        setLoading(false);
      });
    return () => ac.abort();
  }, [url]);

  if (errored && !data) return null;
  if (loading && !data) {
    return (
      <div className={className ?? 'mt-2 rounded-lg border border-ink-200 bg-white/40 p-2 text-xs text-ink-500 dark:border-ink-700 dark:bg-ink-900/30 dark:text-ink-300'}>
        Loading preview…
      </div>
    );
  }
  if (!data) return null;

  const hostname = (() => {
    try { return new URL(data.url).hostname.replace(/^www\./, ''); } catch { return data.url; }
  })();

  return (
    <a
      href={data.url}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className={className ?? 'mt-2 flex max-w-md gap-3 overflow-hidden rounded-lg border border-ink-200 bg-white/70 p-2 text-left transition hover:bg-white dark:border-ink-700 dark:bg-ink-900/60 dark:hover:bg-ink-900'}
    >
      {data.image && (
        <img
          src={data.image}
          alt=""
          className="h-16 w-16 shrink-0 rounded-md object-cover"
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs uppercase tracking-wide text-ink-500 dark:text-ink-300">{data.siteName ?? hostname}</div>
        {data.title && <div className="line-clamp-1 text-sm font-medium">{data.title}</div>}
        {data.description && <div className="line-clamp-2 text-xs text-ink-500 dark:text-ink-300">{data.description}</div>}
      </div>
    </a>
  );
}
